import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Shield, Plus, Trash2 } from 'lucide-react';
import { Button, Input, TextArea, SearchSelect } from '../shared';

const iconOptions = [
 { value: 'Shield', label: 'Shield' },
 { value: 'Lock', label: 'Lock' },
 { value: 'Eye', label: 'Eye' },
 { value: 'Database', label: 'Database' },
 { value: 'Users', label: 'Users' },
 { value: 'FileText', label: 'File Text' },
 { value: 'Globe', label: 'Globe' },
 { value: 'Bell', label: 'Bell' },
 { value: 'UserCheck', label: 'User Check' },
 { value: 'AlertCircle', label: 'Alert Circle' },
];

const CreatePrivacyPolicyModal = ({
 isOpen,
 onClose,
 onSave,
 section = null,
 isEdit = false,
 isLoading = false
}) => {
 const [formData, setFormData] = useState({
 title: '',
 content: [],
 icon: 'Shield',
 sort_order: 0,
 is_active: true,
 });
 const [errors, setErrors] = useState({});
 const [newSubtitle, setNewSubtitle] = useState('');
 const [newText, setNewText] = useState('');

 useEffect(() => {
 if (isEdit && section) {
 setFormData({
 title: section.title || '',
 content: section.content || [],
 icon: section.icon || 'Shield',
 sort_order: section.sort_order || 0,
 is_active: section.is_active ?? true,
 });
 } else {
 setFormData({
 title: '',
 content: [],
 icon: 'Shield',
 sort_order: 0,
 is_active: true,
 });
 }
 setErrors({});
 setNewSubtitle('');
 setNewText('');
 }, [isEdit, section, isOpen]);

 const handleChange = (e) => {
 const { name, value, type, checked } = e.target;
 setFormData(prev => ({
 ...prev,
 [name]: type === 'checkbox' ? checked : value
 }));

 if (errors[name]) {
 setErrors(prev => ({
 ...prev,
 [name]: ''
 }));
 }
 };

 const handleIconChange = (value) => {
 setFormData(prev => ({ ...prev, icon: value }));
 if (errors.icon) {
 setErrors(prev => ({ ...prev, icon: '' }));
 }
 };

 const handleAddContent = () => {
 if (newText.trim()) {
 setFormData(prev => ({
 ...prev,
 content: [...prev.content, {
 subtitle: newSubtitle.trim() || null,
 text: newText.trim()
 }]
 }));
 setNewSubtitle('');
 setNewText('');
 }
 };

 const handleRemoveContent = (index) => {
 setFormData(prev => ({
 ...prev,
 content: prev.content.filter((_, i) => i !== index)
 }));
 };

 const validateForm = () => {
 const newErrors = {};
 if (!formData.title.trim()) newErrors.title = 'Title is required';
 if (!formData.content || formData.content.length === 0) {
 newErrors.content = 'At least one content item is required';
 }
 setErrors(newErrors);
 return Object.keys(newErrors).length === 0;
 };

 const handleSubmit = (e) => {
 e.preventDefault();
 if (!validateForm()) {
 return;
 }

 const submitData = {
 ...formData,
 sort_order: parseInt(formData.sort_order) || 0,
 };

 onSave(submitData);
 };

 const handleClose = () => {
 setErrors({});
 onClose();
 };

 if (!isOpen) return null;

 return createPortal(
 <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10001] p-2 sm:p-4 overflow-y-auto">
 <div className="bg-surface rounded-lg shadow-sm max-w-4xl w-full max-h-[95vh] overflow-y-auto relative my-2 sm:my-4">
 {/* Modal Header */}
 <div className="flex items-center justify-between p-3 sm:p-4 md:p-6 border-b border-gray-200 sticky top-0 bg-surface z-10">
 <div className="flex items-center">
 <div className="flex-shrink-0 h-6 w-6 sm:h-8 sm:w-8 rounded-full bg-gradient-to-r from-ink to-neutral-900 flex items-center justify-center">
 <Shield className="h-3 w-3 sm:h-4 sm:w-4 text-white" />
 </div>
 <h3 className="ml-2 sm:ml-3 text-base sm:text-lg font-medium text-gray-900 truncate">
 {isEdit ? 'Edit Privacy Policy Section' : 'Create Privacy Policy Section'}
 </h3>
 </div>
 <button
 onClick={handleClose}
 className="text-gray-400 hover:text-gray-600 p-1"
 >
 <X className="h-4 w-4 sm:h-5 sm:w-5" />
 </button>
 </div>

 {/* Form */}
 <form onSubmit={handleSubmit} className="p-3 sm:p-4 md:p-6">
 <div className="space-y-4 sm:space-y-6">
 {/* Title */}
 <div>
 <label className="block text-sm font-medium text-gray-700 mb-1">
 Title <span className="text-red-500">*</span>
 </label>
 <Input
 name="title"
 value={formData.title}
 onChange={handleChange}
 placeholder="e.g., 1. Information We Collect"
 error={errors.title}
 required
 />
 </div>

 {/* Icon and Sort Order */}
 <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
 <div>
 <label className="block text-sm font-medium text-gray-700 mb-1">
 Icon
 </label>
 <SearchSelect
 name="icon"
 value={formData.icon}
 onChange={handleIconChange}
 options={iconOptions}
 placeholder="Select an icon"
 error={errors.icon}
 />
 </div>
 <div>
 <label className="block text-sm font-medium text-gray-700 mb-1">
 Sort Order
 </label>
 <Input
 name="sort_order"
 type="number"
 value={formData.sort_order}
 onChange={handleChange}
 placeholder="0"
 min="0"
 error={errors.sort_order}
 />
 </div>
 </div>

 {/* Content Items */}
 <div>
 <label className="block text-sm font-medium text-gray-700 mb-1">
 Content Items <span className="text-red-500">*</span>
 </label>
 <div className="space-y-3 mb-3">
 {formData.content.map((item, index) => (
 <div key={index} className="bg-surface-muted p-3 rounded-lg border border-gray-200">
 <div className="flex items-start justify-between gap-2 mb-2">
 <div className="flex-1">
 {item.subtitle && (
 <h4 className="text-sm font-semibold text-gray-900 mb-1">
 {item.subtitle}
 </h4>
 )}
 <p className="text-sm text-gray-700">{item.text}</p>
 </div>
 <button
 type="button"
 onClick={() => handleRemoveContent(index)}
 className="text-red-600 hover:text-red-800 p-1 flex-shrink-0"
 >
 <Trash2 className="w-4 h-4" />
 </button>
 </div>
 </div>
 ))}
 </div>
 <div className="space-y-2 border border-gray-200 rounded-lg p-3 bg-surface-muted">
 <Input
 value={newSubtitle}
 onChange={(e) => setNewSubtitle(e.target.value)}
 placeholder="Subtitle (optional)"
 className="text-sm"
 />
 <TextArea
 value={newText}
 onChange={(e) => setNewText(e.target.value)}
 placeholder="Enter content text..."
 rows="3"
 className="text-sm"
 />
 <Button
 type="button"
 onClick={handleAddContent}
 variant="outline"
 size="sm"
 disabled={!newText.trim()}
 >
 <Plus className="w-4 h-4 mr-1" />
 Add Content Item
 </Button>
 </div>
 {errors.content && (
 <p className="mt-1 text-sm text-red-600">{errors.content}</p>
 )}
 </div>

 {/* Active Status */}
 <div className="flex items-center">
 <input
 id="is_active"
 name="is_active"
 type="checkbox"
 checked={formData.is_active}
 onChange={handleChange}
 className="h-4 w-4 text-ink border-gray-300 rounded focus:ring-ink"
 />
 <label htmlFor="is_active" className="ml-2 block text-sm text-gray-900">
 Active
 </label>
 </div>
 </div>

 {/* Modal Footer */}
 <div className="mt-6 p-3 sm:p-4 md:p-6 border-t border-gray-200 flex justify-end space-x-3 sticky bottom-0 bg-surface z-10">
 <Button
 type="button"
 variant="outline"
 onClick={handleClose}
 disabled={isLoading}
 >
 Cancel
 </Button>
 <Button
 type="submit"
 variant="primary"
 loading={isLoading}
 className="w-full sm:w-auto"
 >
 {isEdit ? 'Update Section' : 'Create Section'}
 </Button>
 </div>
 </form>
 </div>
 </div>,
 document.body
 );
};

export default CreatePrivacyPolicyModal;

